import { useMemo } from "react";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import styles from "./RulePanel.module.css";
import { ToggleSwitch } from "../../../../shared";
import { AppStrings } from "../../../../../utils/constants/AppStrings";

const SUBTITLE_TONE_CLASS = {
  warning: styles.subtitleWarning,
  muted: styles.subtitleMuted,
};

export default function RulePanel({
  title,
  titleId,
  tooltip,
  tooltipLabel,
  subtitle,
  subtitleTone = "warning",
  allOnLabel = AppStrings.TIER_1_ALL_ON,
  toggleAriaPrefix = AppStrings.TIER_1_RULE_TOGGLE_ARIA,
  rules,
  enabledMap,
  invalidMap,
  onToggleRule,
  onToggleAll,
  renderConfiguration,
  testIdPrefix,
  columnLabels,
  // When set (non-empty string), the panel is treated as locked by
  // an external constraint (currently: Tier 2 gated behind Tier 1
  // having at least one enabled rule). Every toggle — master and
  // per-row — is disabled, and the message replaces the normal
  // subtitle with muted styling. Field values in the configuration
  // cells stay controlled by `renderConfiguration`; the RulesTab
  // wiring already disables those inputs when the row's `enabled`
  // is false, and locking always follows a cascade to `enabled:
  // false`, so no extra plumbing is needed here.
  lockedMessage = null,
}) {
  const allOn = useMemo(
    () => rules.every((rule) => !!enabledMap[rule.id]),
    [rules, enabledMap],
  );

  const isLocked = Boolean(lockedMessage);

  // When locked, swap the normal subtitle for the lock explanation
  // in muted tone. Otherwise keep the caller-provided subtitle and
  // tone.
  const displayedSubtitle = isLocked ? lockedMessage : subtitle;
  const displayedTone = isLocked ? "muted" : subtitleTone;
  const subtitleClassName = `${styles.panelSubtitle} ${
    SUBTITLE_TONE_CLASS[displayedTone] ?? SUBTITLE_TONE_CLASS.warning
  }`;

  return (
    <section
      className={styles.panel}
      aria-labelledby={titleId}
      data-locked={isLocked ? "true" : undefined}
    >
      <header className={styles.panelHeader}>
        <div className={styles.panelHeaderLeft}>
          <div className={styles.titleRow}>
            <h2 id={titleId} className={styles.panelTitle}>
              {title}
            </h2>
            <Tooltip title={tooltip}>
              <InfoCircleOutlined
                className={styles.titleInfoIcon}
                aria-label={tooltipLabel}
                data-testid={`${testIdPrefix}-info`}
              />
            </Tooltip>
          </div>
          {displayedSubtitle && (
            <p
              className={subtitleClassName}
              data-testid={
                isLocked ? `${testIdPrefix}-locked-message` : undefined
              }
              role={isLocked ? "status" : undefined}
            >
              {displayedSubtitle}
            </p>
          )}
        </div>
        <div className={styles.allOnControl}>
          <span className={styles.allOnLabel}>{allOnLabel}</span>
          <span
            className={styles.toggleSlot}
            data-testid={`${testIdPrefix}-master-toggle`}
          >
            <ToggleSwitch
              value={allOn}
              // Belt-and-braces alongside the `disabled` attribute
              // below: JSDOM (and some assistive tooling) can
              // synthesise a `change` event on a disabled checkbox
              // even though real browsers suppress it. Refusing at
              // the callback boundary keeps the invariant intact
              // regardless of the event surface.
              onChange={isLocked ? undefined : onToggleAll}
              disabled={isLocked}
            />
          </span>
        </div>
      </header>

      <div className={styles.tableWrapper}>

        <div className={styles.tableHeader}>
          <span className={styles.columnLabel}>{columnLabels.rule}</span>
          <span className={styles.columnLabel}>
            {columnLabels.configuration}
          </span>
          <span
            className={`${styles.columnLabel} ${styles.columnLabelStatus}`}
          >
            {columnLabels.status}
          </span>
        </div>

        <ul className={styles.ruleList}>
          {rules.map((rule) => {
            const isEnabled = !!enabledMap[rule.id];
            const isInvalid = !!invalidMap?.[rule.id];
            const rowClassName = isInvalid
              ? `${styles.ruleRow} ${styles.ruleRowInvalid}`
              : styles.ruleRow;
            return (
              <li
                key={rule.id}
                className={rowClassName}
                data-invalid={isInvalid ? "true" : undefined}
                aria-invalid={isInvalid || undefined}
                data-testid={`${testIdPrefix}-rule-${rule.id}-row`}
              >
                <div className={styles.ruleCell}>
                  <span className={styles.ruleName}>{rule.name}</span>
                  <span className={styles.ruleDescription}>
                    {rule.description}
                  </span>
                </div>
                <div className={styles.configurationCell}>
                  {renderConfiguration(rule.id)}
                </div>
                <div className={styles.statusCell}>
                  <span
                    className={styles.toggleSlot}
                    data-testid={`${testIdPrefix}-rule-${rule.id}-toggle`}
                    aria-label={`${toggleAriaPrefix}: ${rule.name}`}
                  >
                    <ToggleSwitch
                      value={isEnabled}
                      // See the equivalent guard on the master
                      // toggle above — refuse the callback at the
                      // boundary so JSDOM's synthetic `change` on
                      // a disabled input can't slip through.
                      onChange={
                        isLocked
                          ? undefined
                          : (checked) => onToggleRule(rule.id, checked)
                      }
                      disabled={isLocked}
                    />
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
